import { useQuery } from "@tanstack/react-query";
import { ApiResponse } from "../../types/api-response";
import { UsageType } from "../../types/bookings";
import axios from "../../utils/axiosInceptor";
import { apiV1Endpoint } from "./endpoints";

export type ConfigItem = "leaseNature";

type FilterByValueMap = {
  usageType: UsageType;
  region: "asia" | "africa";
};

type FilteredOptions = {
  [K in keyof FilterByValueMap]: {
    configItem: ConfigItem;
    filterBy: K;
    value: FilterByValueMap[K];
  };
}[keyof FilterByValueMap];

type UnfilteredOptions = {
  configItem: ConfigItem;
  filterBy?: undefined;
  value?: undefined;
};

export type GetMasterConfigOptions = FilteredOptions | UnfilteredOptions;

export type MasterConfigResult = Record<string, string>;

export type UseGetMasterConfigOptions = GetMasterConfigOptions & {
  isEnabled?: boolean;
};

const getMasterConfig = async (options: GetMasterConfigOptions) => {
  const { configItem, filterBy, value } = options;
  const params = new URLSearchParams();

  params.append("configItem", configItem);
  if (filterBy) params.append("filterBy", filterBy);
  if (value) params.append("value", value);

  const url = `${apiV1Endpoint}/getMasterConfigData`;
  const response = await axios.get<ApiResponse<MasterConfigResult>>(url, { params });
  return response.data.result;
};

export const useGetMasterConfig = (options: UseGetMasterConfigOptions) => {
  const { isEnabled, ...restProps } = options;

  const { data, isLoading, isSuccess } = useQuery({
    queryKey: ["master-config", restProps.configItem, restProps.filterBy, restProps.value],
    queryFn: () => getMasterConfig(restProps),
    enabled: isEnabled,
    refetchOnWindowFocus: false,
  });

  return {
    masterConfig: data,
    isLoadingMasterConfig: isLoading,
    isSuccessMasterConfig: isSuccess,
  };
};
